import React from 'react';
import styled from 'styled-components';

import Text from '~/components/Text';
import H1 from '~/components/Headline';
import Closer from './Closer';

const Headline = styled(H1)`
  margin-top: 30px;
  margin-bottom: 15px;
`;

function FAQ({ setInfoboxContent }) {
  return (
    <>
      <Closer setInfoboxContent={setInfoboxContent} />
      <Headline style={{ marginTop: 0 }}>Was zeigt die Grafik?</Headline>
      <Text>
        Die Grafik zeigt die Verteilung der gezahlten Steuern und Sozialabgaben in den verschiedenen Altersgruppen in
        Deutschland auf der einen Seite und die Leistungen des Staates an die verschiedenen Altersgruppen auf der
        anderen Seite. Auf der y-Achse sind die derzeit lebenden Jahrgänge eingetragen. Auf der x-Achse ist zu sehen,
        wie viel diese Jahrgänge durchschnittlich im Jahr 2021 an staatlichen Abgaben zahlen (linke Seite) und wie viel
        sie im Gegenzug durchschnittlich an Leistungen vom Staat erhalten (rechte Seite). Der Balken der geleisteten
        Abgaben ist unterteilt in die verschiedenen Abgabenarten Einkommensteuer, Solidaritätszuschlag, Mehrwertsteuer,
        Tabak- / Alkohol- / Wettsteuer, Energiesteuern / EEG-Umlage, Arbeitslosenversicherung, Krankenversicherung
        (gesetzlich), Krankenversicherung (privat), Pflegeversicherung, Rentenversicherung, unterstellte Pensionsabzüge
        und sonstige Abgaben. Der Balken der erhaltenen Leistungen ist unterteilt in Renten, Pensionen, Arbeitslosengeld
        I, Kindergeld, Elterngeld / Mutterschaftsgeld, Gesundheit, Kinderbetreuung, Schulen, Hochschulen,
        Arbeitslosengeld II, Grundsicherung im Alter, Wohngeld, Bafög und sonstige Leistungen.
      </Text>

      <Headline>Was lässt sich mit der Grafik machen?</Headline>

      <Text>
        Oberhalb der Grafik lässt sich das Alter auswählen, das in der Grafik hervorgehoben werden soll. Für dieses
        Alter werden unter der Grafik die durchschnittlich geleisteten Abgaben und die durchschnittlich erhaltenen
        Leistungen jährlich pro Kopf in Euro aufgelistet. Unterhalb der Grafik lässt sich außerdem die Auswahl von
        “jährlich pro Kopf in Euro” auf “jährlich je Jahrgang in Mrd. Euro” umstellen. Die Ausgaben je Jahrgang zeigen
        die Gesamtsumme der jährlich geleisteten Abgaben und erhaltenen Leistungen einer Altersgruppe in Milliarden
        Euro. Oberhalb der Grafik lässt sich auch die Bevölkerungsgruppe auswählen, zum Beispiel nur Frauen,
        Westdeutsche, Großstädter und Menschen mit Hochschulabschluss. Für beide Seiten der Grafik lässt sich die
        Anzeige zwischen “geleistete Abgaben” und “erhaltene Leistungen” hin- und herstellen, so dass sich die
        geleisteten Abgabe bzw. die erhaltenen Leistungen verschiedener Bevölkerungsgruppen miteinander vergleichen
        lassen.{' '}
      </Text>

      <Headline>Was sind geleistete Abgaben?</Headline>

      <Text>
        Abgaben sind alle Zahlungen privater Haushalte an staatliche Stellen, zum Beispiel Steuern und
        Sozialversicherungsbeiträge. Nicht enthalten sind Unternehmenssteuern, etwa die Gewerbesteuer und die
        Körperschaftsteuer auf Unternehmensgewinne. Außerdem fehlen Steuern, die Unternehmen auf Vorleistungen sowie für
        Produktionsstätten und Produktionsmittel zahlen, zum Beispiel Energiesteuern, Kfz-Steuer, Grundsteuer und
        Mehrwertsteuer. Außerdem werden in der Grafik die Arbeitgeberbeiträge zur Sozialversicherung nicht
        berücksichtigt, weil unklar ist, ob diese eher die Unternehmensgewinne oder die Löhne belasten. Auch die
        Kirchensteuer bleibt außen vor, weil diese nur Kirchenmitglieder betrifft.{' '}
      </Text>

      <Headline>Was sind erhaltene Leistungen?</Headline>

      <Text>
        Zu den erhaltenen staatlichen Leistungen gehören Ansprüche an die gesetzliche Rentenversicherung, an
        Pensionskassen und an die Arbeitslosenversicherung, die im Laufe des Erwerbslebens durch Beiträge erworben
        werden, außerdem Sozialleistungen nach Bedürftigkeit ohne Gegenleistung. Eine Besonderheit bildet das BAföG
        (Bundesausbildungsförderungsgesetz), das zur einen Hälfte als Sozialleistung und zur anderen Hälfte als Darlehen
        gewährt wird. Darüber hinaus zeigt die Grafik nicht-geldliche Leistungen, die im Bildungswesen und im
        Gesundheitswesen erbracht werden. Andere Leistungen wie die öffentliche Infrastruktur und Pflegeleistungen
        werden nicht erfasst.
      </Text>

      <Headline>Auf welchen Daten basiert die Auswertung?</Headline>

      <Text>
        Die Berechnungen basieren auf Daten des Sozio-oekonomischen Panels (SOEP). Das SOEP ist eine seit 1984
        durchgeführte repräsentative Wiederholungsbefragung in Deutschland von derzeit rund 38.000 Personen aus mehr als
        14.000 Haushalten. Aktuell sind die SOEP-Daten bis zum Jahr 2019 verfügbar, wobei sich die Einkommen – entgegen
        der sozio-demografischen Merkmale – auf das jeweilige Vorjahr beziehen, also 2018.{' '}
      </Text>

      <Headline>Was wurde mit den Daten gemacht?</Headline>

      <Text>
        Die geldlichen Transfereinkommen werden im SOEP abgefragt und wurden so übernommen. Auf Grundlage der im SOEP
        genannten Einkommen wurden darüber hinaus die direkten Steuern und die Sozialbeiträge ermittelt. Die indirekten
        Steuern wurden mithilfe der Einkommens- und Verbrauchsstichprobe 2013 simuliert, bis ins Jahr 2021
        fortgeschrieben und mit den SOEP-Daten verknüpft. Die staatlichen Sachleistungen pro Kopf erhebt das
        Statistische Bundesamt. Diese Daten wurden den entsprechenden Altersgruppen zugeordnet. Weil in einigen
        Jahrgängen nur wenige Personen befragt wurden, können sich besondere Merkmale weniger Befragter auf das
        Endergebnis auswirken. Um die Bedeutung dieser Ausreißer nicht überzubewerten, wurden die Kurven statistisch
        geglättet.
      </Text>

      <Headline>Wie sind Großstadt, Stadt und Land definiert?</Headline>

      <Text>
        Zur Vergleichsgruppe Großstadt gehören alle Personen, die in einer Stadt mit mehr als 100.000 Einwohnern leben.
        Zur Gruppe Stadt gehören alle ab 20.000 Einwohnern. Entsprechend gehören alle anderen Personen zur Gruppe Land.
      </Text>

      <Headline>Wie sind Westdeutsche und Ostdeutsche definiert?</Headline>

      <Text>
        Zur Vergleichsgruppe Ost gehören alle Personen mit Wohnsitz in einem der ostdeutschen Bundesländer inklusive
        Berlin. Zur Gruppe West gehören entsprechend alle anderen.
      </Text>

      <Headline>Wie sind die Einkommensviertel definiert?</Headline>

      <Text>
        Dafür wird zunächst aus dem Bruttoeinkommen, der Anzahl der Erwachsenen und der Kinder im Haushalt das
        sogenannte bedarfsgewichtete Einkommen ermittelt. Dafür wird das gesamte jährliche Haushaltsbruttoeinkommen
        durch die bedarfsgewichtete Zahl der Haushaltsmitglieder geteilt. Der erste Erwachsene hat den Faktor 1, jedes
        weitere Haushaltsmitglied über 14 Jahren den Faktor 0,5, Kinder unter 14 Jahren den Faktor 0,3. Damit wird
        berücksichtigt, dass Kinder weniger Geld brauchen als Erwachsene, und dass das Leben in einigen Bereichen
        günstiger wird, wenn mehrere Menschen zusammenleben. In einem zweiten Schritt werden alle Haushalte in
        Deutschland nach der Höhe dieses Einkommens von Arm nach Reich aufgereiht. Die ärmsten 25 Prozent bilden das
        untere Einkommensviertel, die folgenden 25 Prozent das zweite Viertel, die darauf folgenden das dritte Viertel
        und die reichsten 25 Prozent das obere Einkommensviertel.
      </Text>

      <Headline>Wie sind die Bildungsniveaus definiert?</Headline>

      <Text>
        Zur Gruppe ohne Ausbildung gehören alle Personen ohne Berufsausbildung, die zudem momentan keine Ausbildung und
        kein Studium absolvieren. Zur zweiten Gruppe mit Ausbildung gehören Personen, die eine Berufsausbildung
        absolviert haben oder sich gerade in Ausbildung befinden. Zur Gruppe mit Hochschulabschluss gehören alle
        Personen mit abgeschlossenem Studium an einer Fachhochschule oder Universität, außerdem alle Personen, die sich
        derzeit im Studium befinden. Minderjährige werden in dieser Betrachtung nicht berücksichtigt, weil noch nicht
        klar ist, welches Bildungsniveau sie erreichen werden.
      </Text>

      <Headline>Wie werden die Gesundheitsleistungen errechnet?</Headline>

      <Text>
        Für die Leistungsausgaben der gesetzlichen Krankenversicherungen werden vom Bundesamt für Soziale Sicherung
        Durchschnitte im Jahr 2019 je Versicherten nach Alter und Geschlecht ausgewiesen. Diese wurden den
        entsprechenden Gruppen in den SOEP-Daten zugeordnet. Für die Privatversicherten wird angenommen, dass diese die
        gleichen Gesundheitskosten nach Alter und Geschlecht verursachen wie gesetzlich Versicherte. Leistungen der
        sozialen Pflegeversicherung werden nicht abgebildet.{' '}
      </Text>

      <Headline>Wie werden die Bildungsleistungen errechnet?</Headline>

      <Text>
        Das Statistische Bundesamt berechnet in seinem Bildungsfinanzbericht die Kosten der öffentlichen Hand für die
        Bildungsleistungen je Schüler und die Betreuungskosten je Kind in Kindertagesstätten für die einzelnen
        Bundesländer und im Fall der Schulen zudem für die einzelnen Schulformen. Diese Werte wurden den betroffenen
        Kindern in den SOEP-Daten zugeordnet. Die Kosten für die Lehre an Hochschulen je Student sind differenziert nach
        Bundesländern und international standardisiert für das Jahr 2018 in der Hochschulfinanzstatistik verfügbar. Eine
        Differenzierung nach Hochschultyp und Studiengang ist damit allerdings nicht möglich.{' '}
      </Text>

      <Headline>Warum ist die private Krankenversicherung enthalten?</Headline>

      <Text>
        Die private Krankenversicherung ist zwar keine staatliche Organisation, aber der Staat verpflichtet die Bürger,
        sich gegen Krankheit zu versichern und schreibt die von der privaten Krankenversicherung zu finanzierenden
        Grundleistungen vor. Deshalb hat die private Krankenversicherung staatlichen Charakter und wird wie andere
        Sozialversicherungen in der Grafik aufgeführt.
      </Text>

      <Headline>Was sind unterstellte Pensionsabzüge?</Headline>

      <Text>
        Beamte zahlen keine Beiträge für ihre spätere Pension. Die Pensionen werden stattdessen direkt aus den
        öffentlichen Haushalten finanziert. Um die Pensionen dennoch mit der gesetzlichen Rentenversicherung vergleichen
        zu können, wurde in dieser Auswertung unterstellt, der Staat würde den Beamten die Aufwendungen für die
        Altersvorsorge zunächst als Gehaltsbestandteil überweisen, um sie dann als Pensionsabzüge wieder abzuziehen.
      </Text>

      <Headline>Warum fehlen Werte für Minderjährige?</Headline>

      <Text>
        Die ganz oder teilweise auf die Minderjährigen entfallenden Transfereinkommen werden im SOEP komplett deren
        Eltern zugerechnet. Zudem werden Einkommen der unter 18-Jährigen (z.B. der Auszubildenden) im SOEP nicht
        berücksichtigt und ihnen somit keine Einkommensteuer und keine Sozialbeiträge zugerechnet. Die Mehrwertsteuer
        wird den Minderjährigen entsprechend des auf sie entfallenden Anteils der Konsumausgaben zugeordnet. Außerdem
        ist bei Minderjährigen noch nicht klar, welches Bildungsniveau sie erreichen werden. In den Vergleichsgruppen
        “ohne Ausbildung”, “mit Ausbildung” und “mit Hochschulabschluss” sind die Jüngeren deshalb nicht enthalten.{' '}
      </Text>

      <Headline>Wie wirkt sich die Pandemie aus?</Headline>

      <Text>
        Die zugrundeliegenden Daten stammen aus dem Jahr 2019. Die Auswirkungen der Corona-Pandemie sind darin also noch
        nicht enthalten. Im Jahr 2020 sind die Lohnersatzleistungen für Kurzarbeiter stark gestiegen, dementsprechend
        höher fiel das Arbeitslosengeld I in den mittleren Jahrgängen aus. Auch Einkommen und Konsumausgaben sind in
        dieser Zeit gesunken und somit auch die Einnahmen aus Einkommensteuer und Mehrwertsteuer (erst recht durch die
        Mehrwertsteuersenkung im zweiten Halbjahr 2020). Im Jahr 2021, für das die Berechnungen gelten, hat sich der
        Arbeitsmarkt und die Wirtschaftsleistung wieder weitgehend erholt, so dass die Erwerbstätigkeit und die
        Konsumausgaben wieder auf einem ähnlichen Niveau gelegen haben dürften wie im Jahr 2019.{' '}
      </Text>
    </>
  );
}

export default FAQ;
